import { useEffect, useState, useRef } from "react";
import html2pdf from "html2pdf.js";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchClinicalService,
  fetchMedicationReview,
  setClinicalServiceName,
} from "../../../../../redux/modules/clinical-services/actions";
import { ClinicalServiceTypes } from "../../../text/data";
import { PatientAcknowledgmentForm } from "../../../../forms/medication-review-forms/PatientAcknowledgmentForm";
import { PersonalMedicationRecordForm } from "../../../../forms/medication-review-forms/PersonalMedicationRecordForm";
import { ProviderNotificationForm } from "../../../../forms/medication-review-forms/ProviderNotificationForm";
import { fetchNotepadByService, fetchNotepad, sendEmailWithPdf, uploadPdf } from "../../../../../redux/modules/patient/actions";
import { displayToast } from "../../../../../redux/modules/toast/actions";
import ConfirmationModalNew from '../../../../common/ConfirmationModalNew';
import { FormContextAMR } from "../../../../forms/form-context";
import { PharmacistsWorksheetForm } from "../../../../forms/medication-review-forms/PharmacistsWorksheetForm";
import { MedicationReviewDocTypes } from "../../service-steps/documentation/data";
import { NotepadFormAMR } from "../../../../forms/medication-review-forms/NotepadFormAMR";
import {
  filterNotesByServiceDate,
  hasUnresolvedFollowUpInterventions,
} from "../../../../../helpers/functions";
import { ReactSVG } from 'react-svg';
import { getPharmacyInfo } from "../../../../forms/common/getPharmacyInfo";

const hasActiveInterventionsFromMedications = (medicationsObj) => {
  return hasUnresolvedFollowUpInterventions(medicationsObj);
};

const DocumentationForm = ({
  clinicalServiceObj,
  clinicalServiceName,
  medications,
  patient,
  formType,
  onBack = () => {},
 }) => {
  
  const dispatch = useDispatch();
  const { id: patientId, serviceId } = useParams();
  const { formsNotepad, notepad } = useSelector((state) => state.patients);
  const location = useLocation();
  const { currentLoggedInUserDetail: userFromCurrent } = useSelector((state) => state.currentLoggedInUserDetail ?? {});
  const { currentLoggedInUserDetail: userFromUsers, pharmacyStores } = useSelector((state) => state.users ?? {});
  const pharmacy = getPharmacyInfo(userFromCurrent ?? userFromUsers, pharmacyStores) ?? {};
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [printMode, setPrintMode] = useState("bw");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const printButtonRef = useRef(null);
  const printableRef = useRef(null);

  const NOTEPAD_FORM_TYPES = new Set([
    MedicationReviewDocTypes.Notepad,
    MedicationReviewDocTypes.NotepadPharmacist,
    MedicationReviewDocTypes.NotepadPatient,
  ]);

  useEffect(() => {
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (serviceId && clinicalServiceName) {
      dispatch(fetchNotepadByService(clinicalServiceName, serviceId));
    }
  }, [serviceId, clinicalServiceName, dispatch]);

  const prevNotepadLength = useRef(notepad?.length || 0);
  useEffect(() => {
    if (serviceId && clinicalServiceName) {
      const currentLength = notepad?.length || 0;
      if (currentLength !== prevNotepadLength.current) {
        prevNotepadLength.current = currentLength;
        const timeoutId = window.setTimeout(() => {
          dispatch(fetchNotepadByService(clinicalServiceName, serviceId));
        }, 1000);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [notepad?.length, serviceId, clinicalServiceName, dispatch]);

  useEffect(() => {
    if (!NOTEPAD_FORM_TYPES.has(formType)) return;

    // Avoid duplicate fetch when service-level effect already handles serviceId
    if (!serviceId && patientId) {
      dispatch(fetchNotepad(patientId));
    }
  }, [formType, patientId, serviceId, dispatch]);
  
  
  function handleNextPage() {
    setCurrentPage((prev) => (prev >= totalPages ? prev : prev + 1));
  }

  function handlePreviousPage() {
    setCurrentPage((prev) => (prev <= 1 ? prev : prev - 1));
  }

  let formComponent = <div>Coming Soon!</div>;
  let isLandscape = false;
  let needsBack = false;
  switch (formType) {
    case MedicationReviewDocTypes.PatientAcknowledgement:
      formComponent = <PatientAcknowledgmentForm />;
      break;
    case MedicationReviewDocTypes.ProviderNotification:
      formComponent = <ProviderNotificationForm />;
      break;
    case MedicationReviewDocTypes.PersonalMedRecord:
      formComponent = <PersonalMedicationRecordForm />;
      isLandscape = true;
      break;
    case MedicationReviewDocTypes.PharmacistWorksheet:
      formComponent = <PharmacistsWorksheetForm />;
      break;
    case MedicationReviewDocTypes.StoryBoard:
      formComponent = <PharmacistsWorksheetForm />;
      break;
    case MedicationReviewDocTypes.Notepad:
    case MedicationReviewDocTypes.NotepadPharmacist:
    case MedicationReviewDocTypes.NotepadPatient:
      formComponent = <NotepadFormAMR />;
      break;
    default:
      needsBack = true;
      break;
  }

  useEffect(() => {
    if (needsBack) onBack();
  }, [needsBack, onBack]);

  if (!clinicalServiceObj || !clinicalServiceName || !patient) {
    return <div>Loading</div>;
  }

  const assessmentForm =
    clinicalServiceObj?.initialAssessment?.initialAssessmentForm || {};
  const completedMedicationUpdate =
    assessmentForm?.documentation?.completeMedicationUpdate === true;
  const hasActiveInterventionsForPmr = completedMedicationUpdate
    ? false
    : hasActiveInterventionsFromMedications(assessmentForm?.medications);

  function handlePrint() {
    if (!printableRef.current) {
      window.print();
      return;
    }

    const html = printableRef.current.innerHTML;
    const newWin = window.open('', '_blank');
    if (!newWin) {
      window.print();
      return;
    }

    const links = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      .map((l) => l.outerHTML)
      .join('\n');
    const styles = Array.from(document.querySelectorAll('style'))
      .map((s) => s.outerHTML)
      .join('\n');

    newWin.document.open();
    newWin.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Print</title>${links}${styles}<style>@page{size: letter ${isLandscape ? "landscape" : "portrait"}; margin: 0.5in;} body{margin:0; padding:0; -webkit-print-color-adjust:exact; print-color-adjust:exact;} .printable-area{width:${isLandscape ? "10in" : "7.5in"}; margin:0 auto;} .printable-area *{max-height:none !important;} </style></head><body>`);
    newWin.document.write(`<div class="print-container printable-area">${html}</div>`);
    newWin.document.write('</body></html>');
    newWin.document.close();
    newWin.focus();
    setTimeout(() => {
      try {
        newWin.print();
      } catch (err) {
        console.error('Print failed', err);
      }
      try { newWin.close(); } catch (e) {}
    }, 500);
  }

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  async function handleSendEmail() {
    const normalizedEmail = (email || "").trim();
    if (!normalizedEmail) {
      setEmailError("Please enter recipient email");
      return;
    }
    if (!isValidEmail(normalizedEmail)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    if (!printableRef.current) {
      setEmailError("Document is not ready to send yet");
      return;
    }

    try {
      setLoading(true);
      setEmailError("");

      const el = printableRef.current;

      // Clone into a fixed off-screen host so html2canvas sees the full form
      // without being constrained by any scroll/overflow/max-height ancestor.
      const clone = el.cloneNode(true);
      const host = document.createElement("div");
      Object.assign(host.style, {
        position: "absolute",
        top: "-99999px",
        left: "0",
        width: isLandscape ? "1086px" : "816px",
        height: "auto",
        overflow: "visible",
        opacity: "0",
        pointerEvents: "none",
        zIndex: "-1",
      });
      host.appendChild(clone);
      document.body.appendChild(host);

      let pdfBlob;
      try {
        pdfBlob = await html2pdf()
          .set({
            margin: 0,
            filename: `${clinicalServiceName || "document"}.pdf`,
            image: { type: "jpeg", quality: 1 },
            html2canvas: { scale: 2, useCORS: true, scrollX: 0, scrollY: 0 },
            jsPDF: {
              unit: "in",
              format: "letter",
              orientation: isLandscape ? "landscape" : "portrait",
            },
          })
          .from(clone)
          .outputPdf("blob");
      } finally {
        document.body.removeChild(host);
      }

      const pdfFilename = `${clinicalServiceName || "document"}.pdf`;
      const pdfLink = await dispatch(uploadPdf(pdfBlob, pdfFilename));

      const formData = new FormData();
      formData.append("File", pdfBlob, pdfFilename);
      formData.append("ToEmail", normalizedEmail);
      formData.append("Subject", clinicalServiceName || "Document");
      formData.append("PatientName", `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim());
      formData.append("FormName", clinicalServiceName || "Document");
      formData.append("PharmacyName", pharmacy.pharmacyName || "");
      formData.append("StoreNumber", pharmacy.pharmacyStoreNumber || "");
      formData.append("PhoneNumber", pharmacy.pharmacyPhone || "");
      formData.append("FaxNumber", pharmacy.pharmacyFax || "");
      formData.append("StoreEmail", pharmacy.pharmacyEmail || "");
      formData.append("PdfLink", pdfLink);

      await dispatch(sendEmailWithPdf(formData));
      dispatch(displayToast('Email sent successfully', 'success', { autoClose: 4000 }));
      setShowEmailModal(false);
      setEmail("");
    } catch (error) {
      setEmailError(error?.message || "Failed to send email");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
    <div className={`p-9 flex flex-row w-full justify-between items-start ${isLandscape ? '' : 'gap-6' }`}>
      <div className={`${isLandscape ? "" : "xl:min-w-[180px]" } print:hidden`}>
        <button onClick={() => onBack()} aria-label="Back" className="flex items-center justify-center">
          <ReactSVG
            src="/icons/back-arrow.svg"
            beforeInjection={(svg) => {
              svg.setAttribute('class', 'w-16 h-16');
            }} 
          />
        </button>         
      </div>
      <div className="relative">
        {currentPage > 1 && (
          <button
            className="absolute -left-[40px] top-1/2"
            onClick={handlePreviousPage}
          >
            <svg
              width="46"
              height="23"
              viewBox="0 0 46 23"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.1667 20.3333L2 11.1667M2 11.1667L11.1667 2M2"
                stroke="#0B4C5B"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        <div
          className={`print-container ${isLandscape ? "overflow-y-auto overflow-x-auto" : ""} ${isLandscape ? "w-[full6px]" : "w-[8.5in]"} ${isLandscape ? "max-h-[calc(100vh-6rem)] min-h-[8.5in]" : "min-h-[11in]"}`}
        >
          <div className={`${isLandscape ? 'min-h-[8.5in]' : 'min-h-[11in]'}`} ref={printableRef}>
          <FormContextAMR.Provider
            value={{
              formType: formType || "",
              clinicalServiceName: clinicalServiceName || "",
              serviceId: serviceId || null,
              patient: patient || {},
              preAssessment:
                clinicalServiceObj?.initialAssessment?.initialAssessmentForm
                  ?.preAssessment || {},
              clinicalAssessment:
                clinicalServiceObj?.initialAssessment?.initialAssessmentForm
                  ?.clinicalAssessment || {},
              documentation:
                clinicalServiceObj?.initialAssessment?.initialAssessmentForm
                  ?.documentation || {},
              education:
                clinicalServiceObj?.initialAssessment?.initialAssessmentForm
                  ?.education || {},
              patientMedications: medications,
              medications:
                clinicalServiceObj?.initialAssessment?.initialAssessmentForm
                  ?.medications || {},
              startDateTime:
                clinicalServiceObj?.initialAssessment?.initialAssessmentForm
                  ?.startDateTime,
              endDateTime:
                clinicalServiceObj?.initialAssessment?.initialAssessmentForm
                  ?.endDateTime,
              hasActiveInterventions: hasActiveInterventionsForPmr,
             
              notepad: (() => {
                const source = (formsNotepad && formsNotepad.length > 0) ? formsNotepad : (notepad || []);
                const params = new URLSearchParams(location.search);
                const serviceDate = params.get('serviceDate');
                return filterNotesByServiceDate(source, serviceDate);
              })()
            }}
          >
            {formComponent}
          </FormContextAMR.Provider>
          </div>
        </div>
        {currentPage < totalPages && (
          <button
            className="absolute -right-[40px] top-1/2"
            onClick={handleNextPage}
          >
            <svg
              width="46"
              height="23"
              viewBox="0 0 46 23"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              transform="scale(-1, 1)"
            >
              <path
                d="M11.1667 20.3333L2 11.1667M2 11.1667L11.1667 2M2"
                stroke="#0B4C5B"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
      <div className="flex flex-col xl:flex-row bg-white print:hidden">
        <button
          aria-label="Print"
          ref={printButtonRef}
          onClick={() => handlePrint()}
          className="flex items-center justify-center relative group p-2 rounded hover:bg-gray-20"
        >
          <ReactSVG
            src="/icons/printer-outline.svg"
            beforeInjection={(svg) => {
              svg.setAttribute('class', 'w-9 h-9');
            }}
          />
          <span className="pointer-events-none absolute top-full mt-3 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-20 text-gray-800 font-semibold shadow rounded py-2 px-4">Print</span>
        </button>

        <div className="hidden xl:block w-px bg-gray-100 self-center h-9" />
        <button
          aria-label="Email"
          onClick={() => {
            setEmailError("");
            setShowEmailModal(true);
          }}
          className="flex items-center justify-center relative group p-2 rounded hover:bg-gray-20"
        >
          <ReactSVG
            src="/icons/mail-open.svg"
            beforeInjection={(svg) => {
              svg.setAttribute('class', 'w-9 h-9');
            }}
          />
          <span className="pointer-events-none absolute top-full mt-3 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-20 text-gray-800 font-semibold shadow rounded py-2 px-4">Email</span>
        </button>
        <div className="hidden xl:block w-px bg-gray-100 self-center h-9" />
        <button aria-label="Fax" className="flex items-center justify-center relative group p-2 rounded hover:bg-gray-20">
          <ReactSVG
            src="/icons/fax-outline.svg"
            beforeInjection={(svg) => {
              svg.setAttribute('class', 'w-9 h-9');
            }}
          />
          <span className="pointer-events-none absolute top-full mt-3 left-1/2 -translate-x-[85%] hidden group-hover:block bg-gray-20 text-gray-800 shadow rounded py-2 px-4 whitespace-nowrap">
            <span className="font-semibold">Email Fax</span> - Coming Soon
          </span>
        </button>
      </div>
    </div>
    <ConfirmationModalNew
      open={showEmailModal}
      title={"Send Email"}
      className={"!w-[400px] !max-w-[400px] !p-0"}
      content={() => (
        <div className="p-2">
          <input
            type="email"
            placeholder="Enter recipient email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError("");
            }}
            className="w-full border border-gray-300 p-2 rounded mb-2"
          />
          {emailError && <p className="text-sm text-red-600 mb-3">{emailError}</p>}
        </div>
      )}
      handleClose={() => { setShowEmailModal(false); setEmailError(""); }}
      onCross={() => { setShowEmailModal(false); setEmailError(""); }}
      confirmText={loading ? "Sending..." : "Send"}
      cancelText={"Cancel"}
      confirmAction={handleSendEmail}
      cancelAction={() => { setShowEmailModal(false); setEmailError(""); }}
      disableConfirm={loading}
      hideCloseIcon={false}
    />
    </>
  );
};

export default DocumentationForm;
